import { useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Breadcrumbs,
  Link,
  Paper,
  Chip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Folder as FolderIcon,
} from "@mui/icons-material";
import { useAppSelector } from "../../../../../redux/store";

export const DrawerNavigation = ({
  selectedFolder,
  navigationStack,
  currentFolder,
  onBackClick,
  onBreadcrumbClick,
}: any) => {
  const userRole = useAppSelector((state) => state.login.user?.role);

  const isAdmin =
    useMemo(() => {
      return userRole && userRole === "admin";
    }, [userRole]) || false;

  const renderBreadcrumbs = () => {
    if (!selectedFolder) return null;

    const breadcrumbItems = [
      <Link
        key="root"
        component="button"
        variant="body2"
        onClick={() => onBreadcrumbClick(-1)}
        sx={{ display: "flex", textDecoration: "none", cursor: "pointer" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <HomeIcon fontSize="small" />
          {isAdmin
            ? selectedFolder.name || "Root"
            : selectedFolder.displayName || "Root"}
        </Box>
      </Link>,
    ];

    // Add folders from navigation stack as clickable breadcrumbs
    if (navigationStack.length > 0) {
      navigationStack.forEach((folder: any, index: any) => {
        breadcrumbItems.push(
          <Link
            key={folder.id || `nav-${index}`}
            component="button"
            variant="body2"
            onClick={() => onBreadcrumbClick(index)}
            sx={{ display: "flex", textDecoration: "none", cursor: "pointer" }}
          >
            {folder.name || "Unnamed"}
          </Link>
        );
      });
    }

    // Add trailing separator without content
    breadcrumbItems.push(
      <Typography key="trailing" variant="body2" color="text.secondary">
        {/* Empty span to show trailing separator */}
      </Typography>
    );

    return (
      <Paper
        elevation={0}
        sx={{
          p: 1,
          bgcolor: "grey.50",
          position: "sticky",
          top: 0,
          zIndex: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Breadcrumbs separator="/" aria-label="breadcrumb">
          {breadcrumbItems}
        </Breadcrumbs>

        {/* Show current folder name below breadcrumbs */}
        {currentFolder && currentFolder.type === "folder" && (
          <Box sx={{ pt: 1, display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FolderIcon color="primary" fontSize="large" />
              <Typography variant="h6" component="h2">
                {currentFolder.name === selectedFolder.name && !isAdmin
                  ? currentFolder.displayName ||
                    currentFolder.name ||
                    "Unnamed Folder"
                  : currentFolder.name || "Unnamed Folder"}
              </Typography>
            </Box>
            <Chip
              label="Folder"
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`${currentFolder.children.length} items`}
              size="small"
              color="default"
              variant="outlined"
            />
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {navigationStack.length > 0 && (
            <IconButton onClick={onBackClick} size="small">
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" component="h1">
            {currentFolder?.type === "file"
              ? "File Details"
              : "Folder Contents"}
          </Typography>
        </Box>
      </Box>

      {/* Breadcrumbs */}
      {selectedFolder && renderBreadcrumbs()}
    </>
  );
};

export default DrawerNavigation;
